import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import HeroSection from './components/HeroSection';
import VendorCarousel from './components/VendorCarousel';
import CategoryShowcase from './components/CategoryShowcase';
import CTASection from './components/CTASection';
import TestimonialSection from './components/TestimonialSection';
import vendorDataService from '../../services/vendorDataService';

const Homepage = () => {
  const navigate = useNavigate();
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState({
    trending: [],
    popularNearYou: [],
    recentlyBooked: [],
    topDJs: [],
    verifiedMakeup: []
  });

  // Load vendors from Supabase
  useEffect(() => {
    const loadVendors = async () => {
      setLoading(true);
      try {
        // Load trending vendors
        const trendingResult = await vendorDataService.getTrendingVendors(5);

        // Load featured vendors
        const featuredResult = await vendorDataService.getFeaturedVendors(8);

        // Load DJs and Music vendors
        const djsResult = await vendorDataService.getVendorsByCategory('music');

        // Load Makeup Artists
        const makeupResult = await vendorDataService.getVendors({
          category: 'makeup',
          isVerified: true,
          limit: 4
        });

        // Transform vendor data to match component props
        const transformVendor = (vendor) => ({
          id: vendor.id,
          name: vendor.business_name,
          category: vendor.category,
          culturalSpecialty: vendor.cultural_specialties?.[0] || vendor.category,
          image: vendor.vendor_gallery?.[0]?.image_url || 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc',
          rating: vendor.average_rating || 0,
          reviewCount: vendor.review_count || 0,
          location: `${vendor.city}, ${vendor.state}`,
          startingPrice: vendor.starting_price,
          priceRange: vendor.price_range || 'Mid-Range',
          isVerified: vendor.is_verified,
          isTrending: vendor.is_featured,
          isFavorited: false
        });

        setVendors({
          trending: trendingResult.success ? trendingResult.data.map(transformVendor) : [],
          popularNearYou: featuredResult.success ? featuredResult.data.slice(0, 3).map(transformVendor) : [],
          recentlyBooked: featuredResult.success ? featuredResult.data.slice(3, 5).map(transformVendor) : [],
          topDJs: djsResult.success ? djsResult.data.slice(0, 2).map(transformVendor) : [],
          verifiedMakeup: makeupResult.success ? makeupResult.data.map(transformVendor) : []
        });
      } catch (error) {
        console.error('Error loading vendors:', error);
      } finally {
        setLoading(false);
      }
    };

    loadVendors();
  }, []);

  useEffect(() => {
    // Auto-detect user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.log('Location access denied');
        }
      );
    }
  }, []);

  const handleSearch = (query) => {
    navigate(`/category-browse?search=${encodeURIComponent(query)}`);
  };

  const handleViewProfile = (vendorId) => {
    navigate(`/vendor-profile`, { state: { vendorId } });
  };

  const handleMessage = (vendorId) => {
    console.log('Message vendor:', vendorId);
    // Implement messaging functionality
  };

  const handleToggleFavorite = (vendorId, isFavorited) => {
    console.log('Toggle favorite:', vendorId, isFavorited);
    // Implement favorite functionality
  };

  const handleViewAllVendors = () => {
    navigate('/category-browse');
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/category-browse?category=${categoryId}`);
  };

  const handleJoinVendor = () => {
    navigate('/vendor-registration');
  };

  const handleBrowseVendors = () => {
    navigate('/category-browse');
  };

  const handlePlanWithAI = () => {
    console.log('AI Planning feature coming soon');
    // Implement AI planning functionality
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <HeroSection onSearch={handleSearch} />

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      )}

      {/* Trending Vendors Carousel */}
      {!loading && vendors.trending.length > 0 && (
        <VendorCarousel
          title="Trending Vendors"
          subtitle="Most popular vendors this month"
          vendors={vendors.trending}
          variant="featured"
          onViewAll={handleViewAllVendors}
          onMessage={handleMessage}
          onViewProfile={handleViewProfile}
          onToggleFavorite={handleToggleFavorite}
        />
      )}

      {/* Popular Near You Carousel */}
      {!loading && vendors.popularNearYou.length > 0 && (
        <VendorCarousel
          title="Popular Near You"
          subtitle={userLocation ? "Based on your location" : "Popular in major cities"}
          vendors={vendors.popularNearYou}
          variant="default"
          onViewAll={handleViewAllVendors}
          onMessage={handleMessage}
          onViewProfile={handleViewProfile}
          onToggleFavorite={handleToggleFavorite}
        />
      )}

      {/* Category Showcase */}
      <CategoryShowcase onCategoryClick={handleCategoryClick} />

      {/* Recently Booked Carousel */}
      {!loading && vendors.recentlyBooked.length > 0 && (
        <VendorCarousel
          title="Recently Booked"
          subtitle="Vendors with recent bookings"
          vendors={vendors.recentlyBooked}
          variant="compact"
          onViewAll={handleViewAllVendors}
          onMessage={handleMessage}
          onViewProfile={handleViewProfile}
          onToggleFavorite={handleToggleFavorite}
        />
      )}

      {/* Top DJs Carousel */}
      {!loading && vendors.topDJs.length > 0 && (
        <VendorCarousel
          title="Top DJs & Musicians"
          subtitle="Highest rated music professionals"
          vendors={vendors.topDJs}
          variant="default"
          onViewAll={() => handleCategoryClick('music')}
          onMessage={handleMessage}
          onViewProfile={handleViewProfile}
          onToggleFavorite={handleToggleFavorite}
        />
      )}

      {/* Verified Makeup Artists Carousel */}
      {!loading && vendors.verifiedMakeup.length > 0 && (
        <VendorCarousel
          title="Verified Makeup Artists"
          subtitle="Certified bridal beauty specialists"
          vendors={vendors.verifiedMakeup}
          variant="default"
          onViewAll={() => handleCategoryClick('makeup')}
          onMessage={handleMessage}
          onViewProfile={handleViewProfile}
          onToggleFavorite={handleToggleFavorite}
        />
      )}

      {/* Testimonials Section */}
      <TestimonialSection />

      {/* CTA Section */}
      <CTASection
        onJoinVendor={handleJoinVendor}
        onBrowseVendors={handleBrowseVendors}
        onPlanWithAI={handlePlanWithAI}
      />

      <Footer />
    </div>
  );
};

export default Homepage;